/* assets/recall-form.js
 *
 * Recall Form
 * -----------
 * Submits to the WSS app proxy (/apps/wss/recall-order). On success, adds
 * the free repair-kit variant returned by the app to the cart (one per
 * cart) and redirects to /cart. If the kit cannot be added, shows the
 * success card with a manual link instead; a successful submission never
 * turns into an error.
 */
(() => {
  function qs(id) {
    return document.getElementById(id);
  }

  function str(v) {
    return v == null ? "" : String(v);
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function scrollToTopSmooth() {
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) { window.scrollTo(0, 0); }
  }

  async function readJsonSafely(res) {
    const txt = await res.text().catch(() => "");
    if (!txt) return {};
    try { return JSON.parse(txt); } catch (_) { return {}; }
  }

  // =========================================================
  // Cart: add each kit product once, then go to the cart
  // =========================================================
  async function addKitsToCartOnce(variantIds, lineProperties) {
    let inCart = [];

    try {
      const cart = await fetch("/cart.js", { credentials: "same-origin" })
        .then((r) => r.json());
      inCart = (cart.items || []).map((item) => item.variant_id || item.id);
    } catch (_) {
      // Cart read failed; attempt the adds anyway.
    }

    const items = variantIds
      .filter((id) => id > 0 && inCart.indexOf(id) === -1)
      .map((id) => {
        const item = { id: id, quantity: 1 };
        if (lineProperties) item.properties = lineProperties;
        return item;
      });

    if (!items.length) return; // everything already in the cart

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({ items: items }),
    });

    if (!res.ok) throw new Error("cart add failed");
  }

  // =========================================================
  // Main
  // =========================================================
  function initRecallForm() {
    const root = qs("rro-root");
    if (!root) return;

    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    const cfg = {
      endpoint: root.dataset.endpoint || "/apps/wss/recall-order",
      defaultPurchaseLocation: root.dataset.defaultPurchaseLocation || "",
      recaptchaEnabled: str(root.dataset.recaptchaEnabled) === "true",
    };

    // A purchase cannot have happened yet. Set at page load rather than in
    // Liquid, so a cached page cannot leave the limit a day behind.
    var rroDate = document.getElementById("rro-purchase-date");
    if (rroDate) rroDate.max = new Date().toISOString().slice(0, 10);

    let eligibleModels = [];
    try {
      const parsed = JSON.parse(root.dataset.models || "[]");
      if (Array.isArray(parsed)) eligibleModels = parsed.map(str).filter(Boolean);
    } catch (_) {
      eligibleModels = [];
    }

    const form = qs("recall-repair-order-form");
    const submitBtn = qs("rro-submit-btn");
    const alertErr = qs("rro-alert-error");
    const alertOk = qs("rro-alert-success");
    const loadingBox = qs("rro-loading");
    if (!form || !submitBtn || !alertErr || !alertOk || !loadingBox) return;

    function showError(msg) {
      alertErr.textContent = msg;
      alertErr.style.display = "block";
      alertOk.style.display = "none";
    }

    function showSuccessCard(msg, productUrl, kitUnavailable) {
      alertErr.style.display = "none";
      alertOk.style.display = "none";
      form.style.display = "none";

      const wrapper = form.closest(".pr-wrapper");

      if (wrapper) {
        const header = wrapper.querySelector(".pr-header");
        if (header) header.style.display = "none";
        const allReq = wrapper.querySelector(".pr-all-required");
        if (allReq) allReq.style.display = "none";
      }

      const existing = document.getElementById("rro-success-card");
      if (existing) existing.remove();

      let kitLine = "";
      if (productUrl) {
        kitLine =
          '<p>We could not add the repair kit to your cart automatically. ' +
          '<a href="' + escapeHtml(productUrl) + '">View the repair kit</a> or check your ' +
          '<a href="/cart">cart</a>.</p>';
      } else if (kitUnavailable) {
        kitLine =
          "<p>We could not locate the free repair kit for your product. " +
          "Please contact customer care and we will get it to you.</p>";
      }

      const card = document.createElement("div");
      card.id = "rro-success-card";
      // Styled by product-registration.css (#rro-success-card rules), which
      // the recall page template already loads.
      card.innerHTML =
        '<div class="review-wrapper">' +
          '<div class="review-header-success">Your repair order has been received!</div>' +
          '<div class="review-body">' +
            '<p>' + escapeHtml(msg || "Thank you! Your repair order has been received.") + '</p>' +
            kitLine +
            '<a href="/">START SHOPPING</a> | <a href="/cart">VIEW CART</a>' +
          '</div>' +
        '</div>';

      if (wrapper) {
        wrapper.appendChild(card);
      } else {
        form.parentNode.insertBefore(card, form.nextSibling);
      }

      scrollToTopSmooth();
    }

    function setLoading(isLoading, text) {
      submitBtn.disabled = !!isLoading;
      loadingBox.style.display = isLoading ? "flex" : "none";
      if (text) {
        const label = loadingBox.querySelector("span");
        if (label) label.textContent = text;
      }
    }

    function resetRecaptcha() {
      if (!cfg.recaptchaEnabled) return;
      if (!window.grecaptcha) return;
      if (window.rroRecaptchaWidgetId == null) return;
      try { window.grecaptcha.reset(window.rroRecaptchaWidgetId); } catch (_) {}
    }

    function validate(formEl) {
      let valid = true;
      const requiredFields = formEl.querySelectorAll(".pr-input[required], .pr-select[required]");

      requiredFields.forEach((field) => {
        const wrapper = field.closest(".pr-field");
        const errorEl = wrapper ? wrapper.querySelector(".pr-error") : null;

        field.classList.remove("pr-invalid");
        if (errorEl) errorEl.style.display = "none";

        if (!field.value || String(field.value).trim() === "") {
          valid = false;
          field.classList.add("pr-invalid");
          if (errorEl) errorEl.style.display = "block";
        }
      });

      const email = formEl.querySelector('input[name="email"]');
      if (email && email.value && !/^\S+@\S+\.\S+$/.test(email.value)) {
        valid = false;
        email.classList.add("pr-invalid");
        const wrapper = email.closest(".pr-field");
        const errorEl = wrapper ? wrapper.querySelector(".pr-error") : null;
        if (errorEl) {
          errorEl.textContent = "Please enter a valid email address.";
          errorEl.style.display = "block";
        }
      }

      // The typed model must exactly match one of the eligible models
      // (the server re-validates; this just gives instant feedback).
      const modelInput = formEl.querySelector('input[name="model_number"]');
      if (modelInput && modelInput.value && eligibleModels.length) {
        const typed = modelInput.value.trim().toUpperCase();
        const matches = eligibleModels.some(function (m) {
          return String(m).toUpperCase() === typed;
        });

        if (!matches) {
          valid = false;
          modelInput.classList.add("pr-invalid");
          const wrapper = modelInput.closest(".pr-field");
          const errorEl = wrapper ? wrapper.querySelector(".pr-error") : null;
          if (errorEl) {
            errorEl.textContent = "Please select an eligible model number from the list.";
            errorEl.style.display = "block";
          }
        }
      }

      return valid;
    }

    // =========================================================
    // Newsletter checkbox -> hidden input (same as registration)
    // =========================================================
    function initNewsletter() {
      const checkbox = qs("newsletterCheckbox");
      const hiddenInput = qs("newsletterValue");
      if (!checkbox || !hiddenInput) return;

      const update = () => (hiddenInput.value = checkbox.checked ? "1" : "0");
      update();
      checkbox.addEventListener("change", update);
    }

    // =========================================================
    // Photo slots (rendered only when the admin toggle is on).
    // Same slot behavior as the wholesale review form: pick shows
    // a preview, the X clears the slot.
    // =========================================================
    function initPhotoSlots() {
      const inputs = form.querySelectorAll(".review-photo-input");

      inputs.forEach(function (input) {
        const slot = input.closest(".review-photo-slot");
        if (!slot) return;

        const preview = slot.querySelector(".review-photo-preview");
        const placeholder = slot.querySelector(".review-photo-ui");
        const removeBtn = slot.querySelector(".review-photo-remove");

        function clearSlot() {
          input.value = "";
          if (preview) {
            preview.src = "";
            preview.style.display = "none";
          }
          if (placeholder) placeholder.style.display = "";
          slot.classList.remove("has-image");
        }

        if (removeBtn) {
          removeBtn.addEventListener("click", function (e) {
            e.preventDefault();
            e.stopPropagation();
            clearSlot();
          });
        }

        input.addEventListener("change", function () {
          const file = input.files && input.files[0];
          if (!file) return;

          if (!file.type || !file.type.startsWith("image/")) {
            alert("Only images allowed");
            clearSlot();
            return;
          }

          const reader = new FileReader();
          reader.onload = function (e) {
            if (preview) {
              preview.src = e.target && e.target.result ? String(e.target.result) : "";
              preview.style.display = "block";
            }
            if (placeholder) placeholder.style.display = "none";
            slot.classList.add("has-image");
          };
          reader.readAsDataURL(file);
        });
      });
    }

    // =========================================================
    // Model type-ahead over the recall program's eligible models
    // =========================================================
    function initModelAutocomplete() {
      const input = qs("rro-model-input");
      const box = qs("rro-model-suggestions");
      const wrap = qs("rro-model-autocomplete");
      if (!input || !box || !wrap) return;

      let activeIndex = -1;
      let items = [];

      function escapeHtml(s) {
        return String(s == null ? "" : s)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      }

      function openBox() { box.classList.add("open"); }
      function closeBox() { box.classList.remove("open"); activeIndex = -1; }

      function setActive(nodes) {
        nodes.forEach(function (n, i) {
          n.classList.toggle("active", i === activeIndex);
        });
      }

      function render(list) {
        items = list;
        box.innerHTML = "";
        if (!items.length) { closeBox(); return; }

        items.forEach(function (model, i) {
          const div = document.createElement("div");
          div.className = "pr-suggestion";
          div.setAttribute("role", "option");
          div.dataset.index = String(i);
          div.innerHTML = "<div><strong>" + escapeHtml(model) + "</strong></div>";

          div.addEventListener("mousedown", function (e) {
            e.preventDefault();
            input.value = model;
            closeBox();
          });

          box.appendChild(div);
        });

        openBox();
      }

      function filterModels() {
        const q = input.value.trim().toUpperCase();
        const list = q
          ? eligibleModels.filter(function (m) {
            return String(m).toUpperCase().indexOf(q) !== -1;
          })
          : eligibleModels.slice();
        render(list.slice(0, 12));
      }

      input.addEventListener("input", filterModels);
      input.addEventListener("focus", filterModels);

      input.addEventListener("keydown", function (e) {
        const nodes = box.querySelectorAll(".pr-suggestion");
        if (!box.classList.contains("open") || !nodes.length) return;

        if (e.key === "ArrowDown") {
          e.preventDefault();
          activeIndex = Math.min(activeIndex + 1, nodes.length - 1);
          setActive(nodes);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          activeIndex = Math.max(activeIndex - 1, 0);
          setActive(nodes);
        } else if (e.key === "Enter") {
          if (activeIndex >= 0 && items[activeIndex] != null) {
            e.preventDefault();
            input.value = items[activeIndex];
            closeBox();
          }
        } else if (e.key === "Escape") {
          closeBox();
        }
      });

      document.addEventListener("click", function (e) {
        if (!wrap.contains(e.target)) closeBox();
      });
    }

    initModelAutocomplete();
    initNewsletter();
    initPhotoSlots();

    form.addEventListener("submit", async function (e) {
      e.preventDefault();

      alertErr.style.display = "none";
      alertOk.style.display = "none";

      if (!validate(form)) {
        showError("Please correct the highlighted fields.");
        scrollToTopSmooth();
        return;
      }

      const formData = new FormData(form);

      // Purchase location is optional. Left blank, the repair order is
      // attributed to our own site: CustomerVue needs a retailer and rejects
      // the submission outright when it has no name.
      if (
        !String(formData.get("purchase_location") || "").trim() &&
        cfg.defaultPurchaseLocation
      ) {
        formData.set("purchase_location", cfg.defaultPurchaseLocation);
      }

      if (cfg.recaptchaEnabled) {
        if (!window.grecaptcha || window.rroRecaptchaWidgetId == null) {
          showError("reCAPTCHA is not ready. Please reload the page and try again.");
          return;
        }

        const token = window.grecaptcha.getResponse(window.rroRecaptchaWidgetId);
        if (!token) {
          showError("Please complete the reCAPTCHA.");
          scrollToTopSmooth();
          return;
        }
        formData.set("g-recaptcha-response", token);
      }

      // Photos present -> multipart (browser sets the boundary); otherwise
      // keep the original urlencoded POST. The app parses both the same way.
      const photoInputs = Array.prototype.slice.call(
        form.querySelectorAll('input[type="file"].review-photo-input'),
      );
      const hasPhotos = photoInputs.some(function (i) {
        return i.files && i.files.length > 0;
      });

      const payload = new URLSearchParams();
      for (const [k, v] of formData.entries()) {
        if (typeof v === "string") payload.set(k, v);
      }

      setLoading(true, "Submitting your repair order…");

      try {
        const res = await fetch(
          cfg.endpoint || form.action,
          hasPhotos
            ? {
                method: "POST",
                headers: { "Accept": "application/json" },
                body: formData,
                credentials: "same-origin",
              }
            : {
                method: "POST",
                headers: {
                  "Accept": "application/json",
                  "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
                body: payload.toString(),
                credentials: "same-origin",
              },
        );

        const data = await readJsonSafely(res);

        const isOk = !!(res.ok && data && data.success === true);
        const message = data && data.message;

        if (isOk) {
          const variantIds = (Array.isArray(data.cart_variant_ids) ? data.cart_variant_ids : [])
            .map((id) => parseInt(String(id), 10))
            .filter((id) => id > 0);

          if (variantIds.length) {
            setLoading(true, "Adding your repair kit to the cart…");
            try {
              // Hidden (underscore) line properties carry the CustomerVue
              // context into the order; the orders/create webhook copies
              // them onto the order's metafields and stamps the claim row
              // (via the claim id) so the claim only counts once ordered.
              const meta = data.recall_meta || {};
              const lineProperties = {
                _wss_recall_model: str(meta.model_number),
                _wss_recall_cv_id: str(meta.customer_vue_id) || "guest",
                _wss_recall_cv_status: str(meta.customer_vue_status) || "guest",
              };
              if (str(meta.claim_id)) {
                lineProperties._wss_recall_claim_id = str(meta.claim_id);
              }
              await addKitsToCartOnce(variantIds, lineProperties);
              window.location.href = "/cart";
              return;
            } catch (_) {
              showSuccessCard(message, str(data.cart_product_url), false);
            }
          } else {
            showSuccessCard(message, "", data.kit_unavailable === true);
          }

          resetRecaptcha();
        } else {
          const msg =
            (message && String(message).trim()) ||
            "Submission failed (HTTP " + res.status + "). Please contact customer care.";

          showError(msg);
          resetRecaptcha();
          scrollToTopSmooth();
        }
      } catch (_) {
        showError("Something went wrong. Please try again.");
        resetRecaptcha();
        scrollToTopSmooth();
      } finally {
        setLoading(false);
      }
    });
  }

  document.addEventListener("DOMContentLoaded", initRecallForm);

  document.addEventListener("shopify:section:load", () => {
    const root = qs("rro-root");
    if (root) root.dataset.initialized = "false";
    initRecallForm();
  });
})();
